"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Button from "@/components/ui/Button";
import {
  captureDemandStripGroupExpanded,
  captureDemandStripIHaveThisClicked,
  captureDemandStripRequestCtaClicked,
  captureDemandStripViewed,
  type DemandStripEntryKind,
} from "@/lib/analytics/captureDemandStrip";
import { HOMEPAGE_COPY } from "@/lib/homepage/copy";
import { getSubcategoryName } from "@/lib/listing/titleComposer";
import {
  areaLabelFromLocation,
  buildSellFromRequestHref,
  DEMAND_STRIP_VISIBLE_ROWS,
  formatPreferenceSummary,
  groupRecentRequestsForDemandStrip,
  type DemandStripArea,
  type DemandStripEntry,
  type DemandStripGroup,
} from "@/lib/item-requests";
import type { ItemRequest } from "@/lib/types";

type HomepageDemandStripProps = {
  requests: ItemRequest[];
  title: string;
  emptyTitle: string;
  emptyBody: string;
  emptyCtaHref: string;
  emptyCtaLabel: string;
  requestItemHref?: string;
  /** Header “Request an item” link (default true). */
  showRequestLink?: boolean;
  /** Empty-state primary CTA (default true). */
  showEmptyCta?: boolean;
};

/** Shared area only — omit “several areas” wallpaper (P3.4). */
function formatSharedAreaText(area: DemandStripArea): string {
  if (area.kind === "shared") return area.label;
  return "";
}

function StripHeader({
  title,
  showRequestLink,
  requestItemHref,
}: {
  title: string;
  showRequestLink: boolean;
  requestItemHref: string;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        <p className="mt-1 text-sm text-text-light">
          {HOMEPAGE_COPY.shelves.recentRequestsAudience}
        </p>
      </div>
      {showRequestLink ? (
        <Link
          href={requestItemHref}
          className="text-sm font-medium text-primary hover:underline"
          onClick={() =>
            captureDemandStripRequestCtaClicked({ surface: "header" })
          }
        >
          {HOMEPAGE_COPY.shelves.recentRequestsEmptyCta} →
        </Link>
      ) : null}
    </div>
  );
}

/** Single-request compact row. */
function RequestRow({
  request,
  subcategoryLabel,
  prefsSummary,
  nested = false,
  entryKind,
  groupSize = null,
  prefsDiverged = false,
  showGroupIHaveThis = true,
}: {
  request: ItemRequest;
  subcategoryLabel: string;
  prefsSummary: string;
  nested?: boolean;
  entryKind: DemandStripEntryKind;
  groupSize?: number | null;
  prefsDiverged?: boolean;
  showGroupIHaveThis?: boolean;
}) {
  const area = areaLabelFromLocation(request.userLocation);
  const meta = [prefsSummary, area].filter(Boolean).join(" · ");
  const sellHref = buildSellFromRequestHref(request);

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 py-3.5 ${
        nested ? "pl-3 border-l-2 border-border" : ""
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold text-foreground leading-snug">
          {subcategoryLabel}
        </p>
        {meta ? (
          <p className="mt-0.5 text-xs text-text-light leading-snug">{meta}</p>
        ) : null}
      </div>
      {/* Button+href does not forward onClick; capture on wrapper before nav. */}
      <span
        className="shrink-0"
        onClick={() =>
          captureDemandStripIHaveThisClicked({
            itemRequestId: request.id,
            entryKind,
            category: request.category,
            subcategory: request.subcategory,
            groupSize,
            prefsDiverged,
            showGroupIHaveThis,
          })
        }
      >
        <Button href={sellHref} variant="primary" size="sm">
          {HOMEPAGE_COPY.shelves.recentRequestsIHaveThis}
        </Button>
      </span>
    </div>
  );
}

function GroupRow({
  group,
  expanded,
  onToggle,
}: {
  group: DemandStripGroup;
  expanded: boolean;
  onToggle: () => void;
}) {
  const areaText = formatSharedAreaText(group.area);
  const collapseLabel = HOMEPAGE_COPY.shelves.recentRequestsCollapse;
  // Count lives in “N open” only — expand CTA has no number (P3.1–P3.2)
  const expandLabel = group.showGroupIHaveThis
    ? HOMEPAGE_COPY.shelves.recentRequestsSeeOptions
    : HOMEPAGE_COPY.shelves.recentRequestsPickMatch;
  const metaParts = [group.commonPrefsSummary, areaText].filter(Boolean);
  const primaryHref = buildSellFromRequestHref(group.primaryRequest);

  const handleExpand = () => {
    if (!expanded) {
      captureDemandStripGroupExpanded({
        subcategory: group.subcategoryId,
        groupSize: group.count,
        hadDirectCta: group.showGroupIHaveThis,
      });
    }
    onToggle();
  };

  return (
    <div className="py-3.5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-foreground leading-snug">
            {group.subcategoryLabel}
            <span className="ml-2 text-sm font-normal text-text-light">
              {HOMEPAGE_COPY.shelves.recentRequestsOpenCount(group.count)}
            </span>
          </p>
          {metaParts.length > 0 ? (
            <p className="mt-0.5 text-xs text-text-light leading-snug">
              {metaParts.join(" · ")}
            </p>
          ) : null}
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2 shrink-0">
          {group.showGroupIHaveThis ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={handleExpand}
                aria-expanded={expanded}
              >
                {expanded ? collapseLabel : expandLabel}
              </Button>
              <span
                onClick={() =>
                  captureDemandStripIHaveThisClicked({
                    itemRequestId: group.primaryRequest.id,
                    entryKind: "group",
                    category: group.primaryRequest.category,
                    subcategory: group.subcategoryId,
                    groupSize: group.count,
                    prefsDiverged: false,
                    showGroupIHaveThis: true,
                  })
                }
              >
                <Button href={primaryHref} variant="primary" size="sm">
                  {HOMEPAGE_COPY.shelves.recentRequestsIHaveThis}
                </Button>
              </span>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={handleExpand}
              aria-expanded={expanded}
            >
              {expanded ? collapseLabel : expandLabel}
            </Button>
          )}
        </div>
      </div>
      {expanded ? (
        <div className="mt-2 space-y-0 divide-y divide-border">
          {group.requests.map((request) => (
            <RequestRow
              key={request.id}
              request={request}
              subcategoryLabel={group.subcategoryLabel}
              prefsSummary={formatPreferenceSummary(
                request.preferenceAttrs ?? {},
              )}
              nested
              entryKind="nested"
              groupSize={group.count}
              prefsDiverged={!group.showGroupIHaveThis}
              showGroupIHaveThis={group.showGroupIHaveThis}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function HomepageDemandStrip({
  requests,
  title,
  emptyTitle,
  emptyBody,
  emptyCtaHref,
  emptyCtaLabel,
  requestItemHref = "/request-item",
  showRequestLink = true,
  showEmptyCta = true,
}: HomepageDemandStripProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const viewedCapturedRef = useRef(false);

  const entries = useMemo(
    () =>
      groupRecentRequestsForDemandStrip(requests, {
        getSubcategoryLabel: (id) => getSubcategoryName(id) || id || "",
      }),
    [requests],
  );

  // Groups first, then singles — one combined row cap (7), then Show more
  const ordered: DemandStripEntry[] = useMemo(() => {
    const groups = entries.filter(
      (e): e is DemandStripGroup => e.kind === "group",
    );
    const chips = entries.filter((e) => e.kind === "chip");
    return [...groups, ...chips];
  }, [entries]);

  const groupCount = ordered.filter((e) => e.kind === "group").length;
  const chipCount = ordered.filter((e) => e.kind === "chip").length;
  const hasMore = ordered.length > DEMAND_STRIP_VISIBLE_ROWS;

  const visibleEntries = showAll
    ? ordered
    : ordered.slice(0, DEMAND_STRIP_VISIBLE_ROWS);

  useEffect(() => {
    if (requests.length === 0 || viewedCapturedRef.current) return;
    viewedCapturedRef.current = true;
    captureDemandStripViewed({
      entryCount: ordered.length,
      groupCount,
      chipCount,
      hasMore,
    });
  }, [requests.length, ordered.length, groupCount, chipCount, hasMore]);

  const header = (
    <StripHeader
      title={title}
      showRequestLink={showRequestLink}
      requestItemHref={requestItemHref}
    />
  );

  if (requests.length === 0) {
    return (
      <div className="w-full pb-6">
        {header}
        <div className="border-t border-border py-8 text-center">
          <p className="text-foreground text-lg font-medium">{emptyTitle}</p>
          <p className="text-text-light mt-2 max-w-md mx-auto">{emptyBody}</p>
          {showEmptyCta ? (
            <div className="mt-6">
              <span
                onClick={() =>
                  captureDemandStripRequestCtaClicked({ surface: "empty" })
                }
              >
                <Button href={emptyCtaHref} className="px-5 py-2.5">
                  {emptyCtaLabel}
                </Button>
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pb-20 sm:pb-6">
      {header}

      <div className="divide-y divide-border border-t border-border">
        {visibleEntries.map((entry) => {
          if (entry.kind === "group") {
            const expanded = expandedIds.has(entry.subcategoryId);
            return (
              <GroupRow
                key={`g-${entry.subcategoryId}`}
                group={entry}
                expanded={expanded}
                onToggle={() => {
                  setExpandedIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(entry.subcategoryId)) {
                      next.delete(entry.subcategoryId);
                    } else {
                      next.add(entry.subcategoryId);
                    }
                    return next;
                  });
                }}
              />
            );
          }
          return (
            <RequestRow
              key={`c-${entry.request.id}`}
              request={entry.request}
              subcategoryLabel={entry.subcategoryLabel}
              prefsSummary={
                entry.prefsSummary ||
                formatPreferenceSummary(entry.request.preferenceAttrs ?? {})
              }
              entryKind="chip"
            />
          );
        })}
      </div>

      {hasMore ? (
        <div className="pt-3">
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => setShowAll((v) => !v)}
            aria-expanded={showAll}
          >
            {showAll
              ? HOMEPAGE_COPY.shelves.recentRequestsShowLess
              : HOMEPAGE_COPY.shelves.recentRequestsShowMore}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
